import React, { useState, useContext } from 'react'
import { Form, Button, InputGroup } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import customColumnService from '../services/customColumnService'
import AuthContext from 'context/auth-context'

function CustomColumnsSection({ dashboardId, disabled = false }) {
  const [newColumnName, setNewColumnName] = useState('')
  const [newColumnType, setNewColumnType] = useState('text')
  const [newSelectOptions, setNewSelectOptions] = useState('')
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)

  // Fetch custom columns
  const { data: customColumns = [], isLoading } = useQuery(
    ['customColumns', dashboardId],
    () => customColumnService.getCustomColumns(token, dashboardId),
    {
      enabled: !!dashboardId && !!token
    }
  )

  // Create custom column mutation
  const createMutation = useMutation(
    (data) => customColumnService.createCustomColumn(token, dashboardId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customColumns', dashboardId])
        queryClient.invalidateQueries(`dashboards/${dashboardId}`)
        setNewColumnName('')
        setNewColumnType('text')
        setNewSelectOptions('')
      }
    }
  )


  const handleCreateColumn = () => {
    if (newColumnName.trim()) {
      // Validate select columns have options
      if (newColumnType === 'select' && !newSelectOptions.trim()) {
        alert('Please provide options for the select column')
        return
      }

      const columnData = {
        name: newColumnName.trim(),
        column_type: newColumnType,
        is_active: true
      }

      // For select columns, add options to settings
      if (newColumnType === 'select' && newSelectOptions.trim()) {
        columnData.settings = {
          options: newSelectOptions
            .split(',')
            .map((option) => option.trim())
            .filter((option) => option.length > 0)
        }
      }

      // For status columns, add predefined options
      if (newColumnType === 'status') {
        columnData.settings = {
          options: ['To-do', 'In progress', 'Complete']
        }
      }

      createMutation.mutate(columnData)
    }
  }


  if (isLoading) {
    return <div className="text-muted">Loading custom columns...</div>
  }

  const hasNameError = customColumns.map(c => c.name).includes(newColumnName)

  return (
    <div className="custom-columns-section">
      <h6 className="fw-bold mb-3">Custom Columns</h6>

      {/* Add new column form */}
      <div className="mb-3">
        <InputGroup className="mb-2">
          <Form.Control
            type="text"
            placeholder="Enter custom column name..."
            value={newColumnName}
            onChange={(e) => setNewColumnName(e.target.value)}
            disabled={disabled || createMutation.isLoading}
          />
          <Form.Select
            value={newColumnType}
            onChange={(e) => setNewColumnType(e.target.value)}
            disabled={disabled || createMutation.isLoading}
            style={{ maxWidth: '120px' }}
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
            <option value="boolean">Checkbox</option>
            <option value="select">Select</option>
            <option value="status">Status</option>
          </Form.Select>
          <Button
            variant="primary"
            onClick={handleCreateColumn}
            disabled={
              disabled ||
              !newColumnName.trim() ||
              (newColumnType === 'select' && !newSelectOptions.trim()) ||
              createMutation.isLoading ||
              hasNameError
            }
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add
          </Button>
        </InputGroup>
        {hasNameError && (
          <div className="mb-3">
            <div className="text-danger small">The new column should have a unique name.</div>
          </div>
        )}

        {/* Options field for select type */}
        {newColumnType === 'select' && (
          <Form.Control
            type="text"
            placeholder="Enter options separated by commas (e.g., Option 1, Option 2, Option 3)"
            value={newSelectOptions}
            onChange={(e) => setNewSelectOptions(e.target.value)}
            disabled={disabled || createMutation.isLoading}
            size="sm"
            className="text-muted"
          />
        )}
      </div>
    </div>
  )
}

export default CustomColumnsSection
